package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.apr.AprAssinatura
import br.com.htms.inspeccaocampo.data.apr.AprDados
import br.com.htms.inspeccaocampo.platform.PlatformServices
import kotlin.math.ceil
import kotlin.math.max
import kotlin.math.min

/**
 * APR em Excel: preenche a planilha modelo (asset `apr_modelo.xlsx`, gerado da aba anotada do
 * "APR_HTMS_SET2026 - MODELO.xlsx") mantendo estilos, logo e configuração de impressão.
 */
object AprXlsxBuilder {
    const val ASSET = "apr_modelo.xlsx"
    const val MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

    private const val LINHA_ETAPA = 20
    private const val ETAPAS_MODELO = 3
    private const val LINHA_EXECUTANTE = 71
    private const val EXECUTANTES_MODELO = 17
    private const val ULTIMA_LINHA_MODELO = 87

    /** Larguras (caracteres) das colunas A..AG do modelo; G é oculta. */
    internal val LARGURAS = mapOf(
        "B" to 9.29, "C" to 6.29, "D" to 15.57, "E" to 15.0, "F" to 8.86, "G" to 0.0, "H" to 4.57, "I" to 4.57,
        "J" to 4.57, "K" to 4.57, "L" to 3.0, "M" to 22.14, "N" to 14.86, "O" to 4.57, "P" to 3.29, "Q" to 2.57,
        "R" to 4.57, "S" to 1.71, "T" to 5.71, "U" to 4.57, "V" to 11.0, "W" to 4.86, "X" to 5.43, "Y" to 4.57,
        "Z" to 4.57, "AA" to 15.0, "AB" to 4.57, "AC" to 5.29, "AD" to 4.14, "AE" to 5.86, "AF" to 6.43, "AG" to 23.57
    )

    suspend fun build(d: AprDados): ByteArray {
        val modelo = PlatformServices.loadBundledAsset(ASSET) ?: error("Modelo da APR não encontrado ($ASSET).")
        return build(d, modelo)
    }

    fun build(d: AprDados, modelo: ByteArray): ByteArray {
        val entradas = ZipLeitor.lerSemCompressao(modelo)
        val folha = FolhaXlsx(entradas.getValue("xl/worksheets/sheet1.xml").decodeToString())

        val nExec = max(EXECUTANTES_MODELO, d.executantes.size)
        val nEtapas = max(1, d.etapas.size)
        folha.repetirLinha(LINHA_EXECUTANTE, EXECUTANTES_MODELO, nExec)
        folha.repetirLinha(LINHA_ETAPA, ETAPAS_MODELO, nEtapas)
        val dT = nEtapas - ETAPAS_MODELO
        val ultima = ULTIMA_LINHA_MODELO + dT + (nExec - EXECUTANTES_MODELO)

        folha.valor("AC2", d.numeroApr)
        folha.valor("AG2", listOf("HTMS ENERGIA", d.ano).filter { it.isNotBlank() }.joinToString(" "))
        folha.valor("AC3", d.numeroSiOs)

        val g = largura("G", "AG")
        identificacao(folha, 6, "G6" to d.atividade, g)
        folha.valor("G7", d.instalacao)
        folha.valor("X7", d.local)
        folha.altura(7, alturaTexto(listOf(d.instalacao to largura("G", "T"), d.local to largura("X", "AG")), 18.75))
        folha.valor("G8", d.emissao)
        folha.valor("Q8", d.horaFormatada)
        folha.valor("X8", d.vigencia)
        identificacao(folha, 9, "G9" to d.responsavel, g)
        identificacao(folha, 10, "G10" to d.substitutos, g)
        identificacao(folha, 11, "G11" to d.maquinas, g)
        identificacao(folha, 12, "G12" to d.episObrigatorios, g)
        identificacao(folha, 13, "G13" to d.episEventuais, g)
        identificacao(folha, 14, "G14" to d.epcsObrigatorios, g)
        identificacao(folha, 15, "G15" to d.epcsEventuais, g)

        val etapas = d.etapas.ifEmpty { listOf(br.com.htms.inspeccaocampo.data.apr.AprEtapa()) }
        etapas.forEachIndexed { i, e ->
            val r = LINHA_ETAPA + i
            folha.numero("B$r", i + 1)
            folha.valor("C$r", e.itemPex)
            folha.valor("E$r", e.etapas)
            folha.valor("K$r", e.perigos)
            folha.valor("N$r", e.pac)
            folha.valor("O$r", e.medidas)
            folha.valor("W$r", e.oQuePodeDarErrado)
            folha.valor("AB$r", e.medidasAdicionais)
            folha.altura(
                r,
                alturaTexto(
                    listOf(
                        e.itemPex to largura("C", "D"), e.etapas to largura("E", "J"), e.perigos to largura("K", "M"),
                        e.pac to largura("N", "N"), e.medidas to largura("O", "V"),
                        e.oQuePodeDarErrado to largura("W", "AA"), e.medidasAdicionais to largura("AB", "AG")
                    ),
                    60.0
                )
            )
        }

        val obsLinha = 23 + dT
        val obs = "Observações complementares:" + if (d.observacoes.isBlank()) "" else " ${d.observacoes.trim()}"
        folha.valor("B$obsLinha", obs)
        folha.altura(obsLinha, alturaTexto(listOf(obs to largura("B", "AG")), 60.0, fonte = 11.0))

        AprAssinatura.entries.forEach { a ->
            val p = d.pessoa(a)
            val r = a.linhaModelo + dT
            folha.valor("B$r", p.nome)
            folha.valor("K$r", p.matricula)
        }

        val inicioExec = LINHA_EXECUTANTE + dT
        for (i in 0 until nExec) {
            val r = inicioExec + i
            val ex = d.executantes.getOrNull(i)
            folha.numero("B$r", i + 1)
            folha.valor("D$r", ex?.nome.orEmpty())
            folha.valor("P$r", ex?.empresa.orEmpty())
        }

        folha.dimensao("B1:AJ$ultima")
        entradas["xl/worksheets/sheet1.xml"] = folha.xml().encodeToByteArray()
        entradas["xl/workbook.xml"] = entradas.getValue("xl/workbook.xml").decodeToString()
            .replace("__FIM__", ultima.toString()).encodeToByteArray()
        return ZipStoreWriter.write(entradas)
    }

    private fun identificacao(folha: FolhaXlsx, linha: Int, celula: Pair<String, String>, largura: Double) {
        folha.valor(celula.first, celula.second)
        folha.altura(linha, alturaTexto(listOf(celula.second to largura), 18.75))
    }

    private fun largura(de: String, ate: String): Double {
        val cols = LARGURAS.keys.toList()
        val a = cols.indexOf(de)
        val b = cols.indexOf(ate)
        return (a..b).sumOf { LARGURAS.getValue(cols[it]) }
    }

    /** Altura (pt) estimada para o texto quebrar dentro das células mescladas. */
    internal fun alturaTexto(textos: List<Pair<String, Double>>, minimo: Double, fonte: Double = 10.0): Double {
        var linhas = 1
        textos.forEach { (t, w) ->
            if (t.isBlank() || w <= 0) return@forEach
            val porLinha = max(1.0, w * 1.15 * 10.0 / fonte)
            val n = t.split('\n').sumOf { par -> max(1, ceil(par.length * 1.08 / porLinha).toInt()) }
            linhas = max(linhas, n)
        }
        return min(409.5, max(minimo, linhas * fonte * 1.32 + 6))
    }
}

/** Edição de uma planilha (sheetN.xml): valores, alturas, repetição de linhas e mesclagens. */
internal class FolhaXlsx(xml: String) {
    private class Linha(var num: Int, var xml: String)
    private data class Faixa(val c1: String, val r1: Int, val c2: String, val r2: Int) {
        fun ref() = "$c1$r1:$c2$r2"
    }

    private val cabeca: String
    private val cauda: String
    private val linhas = ArrayList<Linha>()
    private val faixas = ArrayList<Faixa>()

    init {
        val ini = xml.indexOf("<sheetData>")
        val fim = xml.indexOf("</sheetData>")
        cabeca = xml.substring(0, ini)
        var resto = xml.substring(fim + "</sheetData>".length)
        Regex("<row\\b[^>]*?(?:/>|>[\\s\\S]*?</row>)").findAll(xml.substring(ini, fim)).forEach { m ->
            val n = Regex("<row\\b[^>]*?\\br=\"(\\d+)\"").find(m.value)!!.groupValues[1].toInt()
            linhas += Linha(n, m.value)
        }
        val merge = Regex("<mergeCells\\b[^>]*>([\\s\\S]*?)</mergeCells>").find(resto)
        if (merge != null) {
            Regex("<mergeCell ref=\"([A-Z]+)(\\d+):([A-Z]+)(\\d+)\"\\s*/>").findAll(merge.groupValues[1]).forEach {
                val g = it.groupValues
                faixas += Faixa(g[1], g[2].toInt(), g[3], g[4].toInt())
            }
            resto = resto.replaceRange(merge.range, "__MERGES__")
        }
        cauda = resto
    }

    private fun renumerar(xml: String, para: Int): String =
        xml.replaceFirst(Regex("(<row\\b[^>]*?\\br=\")\\d+\""), "$1$para\"")
            .replace(Regex("(<c r=\"[A-Z]+)\\d+\""), "$1$para\"")

    /**
     * Troca as [qtd] linhas a partir de [inicio] por [n] cópias da linha [inicio]
     * (com as mesclagens dela) e desloca o restante.
     */
    fun repetirLinha(inicio: Int, qtd: Int, n: Int) {
        val delta = n - qtd
        val prototipo = linhas.first { it.num == inicio }.xml
        val faixasProto = faixas.filter { it.r1 == inicio && it.r2 == inicio }
        linhas.removeAll { it.num >= inicio && it.num < inicio + qtd }
        faixas.removeAll { it.r1 >= inicio && it.r1 < inicio + qtd }
        linhas.forEach { if (it.num >= inicio + qtd) { it.num += delta; it.xml = renumerar(it.xml, it.num) } }
        for (i in faixas.indices) {
            val f = faixas[i]
            if (f.r1 >= inicio + qtd) faixas[i] = f.copy(r1 = f.r1 + delta, r2 = f.r2 + delta)
        }
        val novas = (0 until n).map { Linha(inicio + it, renumerar(prototipo, inicio + it)) }
        linhas.addAll(novas)
        linhas.sortBy { it.num }
        (0 until n).forEach { i -> faixasProto.forEach { faixas += it.copy(r1 = inicio + i, r2 = inicio + i) } }
    }

    private fun linha(ref: String): Linha? {
        val n = ref.dropWhile { it.isLetter() }.toInt()
        return linhas.firstOrNull { it.num == n }
    }

    private fun trocarCelula(ref: String, conteudo: (estilo: String) -> String) {
        val l = linha(ref) ?: return
        val re = Regex("<c r=\"$ref\"([^>]*?)(?:/>|>[\\s\\S]*?</c>)")
        val m = re.find(l.xml) ?: return
        val estilo = Regex("\\bs=\"(\\d+)\"").find(m.groupValues[1])?.let { " s=\"${it.groupValues[1]}\"" } ?: ""
        l.xml = l.xml.replaceRange(m.range, conteudo(estilo))
    }

    fun valor(ref: String, texto: String) {
        val t = texto.trim()
        trocarCelula(ref) { s ->
            if (t.isEmpty()) "<c r=\"$ref\"$s/>"
            else "<c r=\"$ref\"$s t=\"inlineStr\"><is><t xml:space=\"preserve\">${esc(t)}</t></is></c>"
        }
    }

    fun numero(ref: String, n: Int) = trocarCelula(ref) { s -> "<c r=\"$ref\"$s><v>$n</v></c>" }

    fun altura(num: Int, pt: Double) {
        val l = linhas.firstOrNull { it.num == num } ?: return
        val v = (kotlin.math.round(pt * 100) / 100.0).toString()
        val tag = Regex("<row\\b[^>]*?>").find(l.xml) ?: return
        var t = tag.value
        t = if (t.contains(" ht=\"")) t.replace(Regex(" ht=\"[^\"]*\""), " ht=\"$v\"") else t.replaceFirst("<row ", "<row ht=\"$v\" ")
        if (!t.contains("customHeight")) t = t.replaceFirst("<row ", "<row customHeight=\"1\" ")
        l.xml = l.xml.replaceRange(tag.range, t)
    }

    private var dimensaoRef: String? = null
    fun dimensao(ref: String) { dimensaoRef = ref }

    fun xml(): String = buildString {
        append(dimensaoRef?.let { cabeca.replace(Regex("<dimension ref=\"[^\"]*\"/>"), "<dimension ref=\"$it\"/>") } ?: cabeca)
        append("<sheetData>")
        linhas.forEach { append(it.xml) }
        append("</sheetData>")
        val merges = if (faixas.isEmpty()) "" else
            "<mergeCells count=\"${faixas.size}\">" + faixas.joinToString("") { "<mergeCell ref=\"${it.ref()}\"/>" } + "</mergeCells>"
        append(cauda.replace("__MERGES__", merges))
    }

    private fun esc(s: String): String = buildString(s.length) {
        for (ch in s) when (ch) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            '\r' -> {}
            else -> if (ch.code < 0x20 && ch != '\n' && ch != '\t') append(' ') else append(ch)
        }
    }
}
